import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Reveal } from "./motion";

const Counter = ({ value, suffix, raw }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) frame = requestAnimationFrame(tick);
      else setN(value);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  const display = raw ? String(n) : n.toLocaleString("en-AU");
  // Support a "$" suffix rendered as a prefix
  if (suffix === "$") return <span ref={ref}>${display}</span>;
  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

export const Impact = ({ overline, title, stats }) => {
  return (
    <section id="impact" data-testid="impact-section" className="relative py-28 lg:py-40 bg-[#010101] overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vw] max-w-[700px] max-h-[700px] rounded-full bg-[#fcc41b]/[0.03] blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-6 text-center">{overline}</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white text-center max-w-4xl mx-auto" style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}>
            {title}
          </h2>
        </Reveal>

        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1c1c1c] border border-[#1c1c1c] rounded-3xl overflow-hidden">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="bg-[#010101]">
              <div className="p-8 lg:p-12 text-center h-full flex flex-col items-center justify-center" data-testid={`stat-${i}`}>
                <div className="font-display font-bold tracking-tighter text-[#fcc41b]" style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
                  <Counter value={s.value} suffix={s.suffix} raw={s.raw} />
                </div>
                <p className="mt-4 text-zinc-400 text-sm max-w-[160px] leading-snug">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
